(() => {
  const loadLazyImages = (section) => {
    section.querySelectorAll('img[data-sa-before-after-lazy]').forEach((image) => {
      if (image.dataset.saBeforeAfterLoaded === 'true') return;

      const srcset = image.dataset.srcset;
      const src = image.dataset.src;
      image.dataset.saBeforeAfterLoaded = 'true';

      if (srcset) {
        image.srcset = srcset;
        image.removeAttribute('data-srcset');
      }

      if (src) {
        image.src = src;
        image.removeAttribute('data-src');
      }
    });
  };

  const initLazyMedia = (section) => {
    if (!section || section.dataset.saBaLazyInit === 'true') return;

    const images = section.querySelectorAll('img[data-sa-before-after-lazy]');
    if (!images.length) return;

    section.dataset.saBaLazyInit = 'true';

    if (!('IntersectionObserver' in window)) {
      loadLazyImages(section);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          loadLazyImages(section);
          observer.disconnect();
        });
      },
      {
        rootMargin: '300px 0px',
        threshold: 0.01,
      }
    );

    observer.observe(section);
  };

  const initSlider = (section) => {
    if (!section || section.dataset.saBaInit === 'true') return;
    section.dataset.saBaInit = 'true';

    const frame = section.querySelector('.sa-before-after__frame');
    const range = section.querySelector('.sa-before-after__range');
    if (!frame || !range) return;

    const min = Number(range.min || 0);
    const max = Number(range.max || 100);
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let hasInteracted = false;
    let hintFrameId = null;

    const updateLabel = (value) => {
      const afterLabel = frame.querySelector('.sa-before-after__label--after');
      if (!afterLabel) return;

      const frameWidth = frame.clientWidth || 0;
      const labelWidth = afterLabel.offsetWidth || 0;
      if (!frameWidth || !labelWidth) return;

      const gap = 8;
      const handlePos = (Number(value) / 100) * frameWidth;
      const spaceRight = frameWidth - handlePos;

      let left = handlePos + gap;
      const maxLeft = frameWidth - labelWidth - gap;

      if (left > maxLeft) left = maxLeft;
      if (left < gap) left = gap;

      frame.style.setProperty('--sa-ba-label-after-left', `${left}px`);

      if (spaceRight < labelWidth + gap * 2) {
        frame.classList.add('sa-before-after__frame--hide-after');
      } else {
        frame.classList.remove('sa-before-after__frame--hide-after');
      }
    };

    const update = (value) => {
      frame.style.setProperty('--sa-ba-position', `${value}%`);
      updateLabel(value);
    };

    const stopHint = () => {
      section.classList.remove('sa-before-after--hint');

      if (hintFrameId) {
        window.cancelAnimationFrame(hintFrameId);
        hintFrameId = null;
      }

      update(range.value);
    };

    const markInteracted = () => {
      if (hasInteracted) return;

      hasInteracted = true;
      stopHint();
    };

    const runHint = () => {
      if (hasInteracted || prefersReducedMotion) return;

      section.classList.add('sa-before-after--hint');

      const baseValue = Number(range.value);
      // Glide in from ~20% to one side, then smoothly settle on the resting
      // position (e.g. from 70% to 50%) to demonstrate the slider.
      const travel = 20;
      const startValue =
        max - baseValue >= travel
          ? Math.min(max, baseValue + travel)
          : Math.max(min, baseValue - travel);
      const duration = 1400;
      let startTime = null;

      const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

      update(startValue.toFixed(2));

      const animate = (timestamp) => {
        if (hasInteracted) return;

        if (startTime === null) {
          startTime = timestamp;
        }

        const progress = Math.min((timestamp - startTime) / duration, 1);
        const eased = easeInOut(progress);
        const nextValue = startValue + (baseValue - startValue) * eased;

        update(nextValue.toFixed(2));

        if (progress < 1) {
          hintFrameId = window.requestAnimationFrame(animate);
          return;
        }

        hintFrameId = null;
        section.classList.remove('sa-before-after--hint');
        update(baseValue);
      };

      hintFrameId = window.requestAnimationFrame(animate);
    };

    // Run the nudge animation the first time the section scrolls into view,
    // instead of a fixed timer after page load (which is missed for sections
    // that only appear below the fold).
    const scheduleHint = () => {
      if (prefersReducedMotion) return;

      if (!('IntersectionObserver' in window)) {
        window.setTimeout(runHint, 500);
        return;
      }

      // Fire once the section has scrolled meaningfully into view. The negative
      // bottom rootMargin means it triggers when the section's top passes ~75%
      // of the viewport height, so it works regardless of how tall the section is.
      const hintObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            hintObserver.disconnect();
            runHint();
          });
        },
        { threshold: 0.01, rootMargin: '0px 0px -25% 0px' }
      );

      hintObserver.observe(section);
    };

    update(range.value);

    scheduleHint();

    range.addEventListener('input', (event) => {
      markInteracted();
      update(event.target.value);
    });

    range.addEventListener('pointerdown', markInteracted, { passive: true });
    range.addEventListener('keydown', markInteracted);

    window.addEventListener('resize', () => update(range.value), { passive: true });
  };

  const initProductAtc = (section) => {
    if (!section || section.dataset.saBaAtcInit === 'true') return;

    const button = section.querySelector('[data-sa-before-after-atc]');
    if (!button) return;

    section.dataset.saBaAtcInit = 'true';

    const buttonText = button.querySelector('[data-sa-before-after-atc-text]');
    const spinner = button.querySelector('.loading__spinner');
    const unavailableLabel = button.dataset.unavailableLabel || 'Unavailable';
    const soldOutLabel = button.dataset.soldOutLabel || unavailableLabel;
    const addLabel = button.dataset.addLabel || unavailableLabel;
    let observedButton = null;
    let buttonObserver = null;

    const getProductRoot = () =>
      document.querySelector('product-info[id^="MainProduct"]') ||
      document.querySelector('product-info') ||
      document;

    const getMainButton = () => {
      const root = getProductRoot();
      return root.querySelector(
        'product-form .product-form__submit[name="add"], product-form button[type="submit"][name="add"], form[data-type="add-to-cart-form"] button[type="submit"][name="add"]'
      );
    };

    // Show our own configurable label instead of mirroring the main button's text.
    // Sold-out / unavailable states still fall back to the translated status labels.
    const getButtonLabel = (sourceButton) => {
      if (!sourceButton) return unavailableLabel;

      // While adding to cart the main button is briefly disabled AND has the
      // `loading` class. Don't mistake that temporary state for "sold out" —
      // keep our own label (the spinner conveys the loading state).
      if (sourceButton.classList.contains('loading')) return addLabel;

      const isDisabled = sourceButton.disabled || sourceButton.getAttribute('aria-disabled') === 'true';
      if (isDisabled) return soldOutLabel;

      return addLabel;
    };

    const setDisabled = (disabled) => {
      button.disabled = disabled;

      if (disabled) {
        button.setAttribute('aria-disabled', 'true');
      } else {
        button.removeAttribute('aria-disabled');
      }
    };

    const sync = () => {
      const sourceButton = getMainButton();

      if (sourceButton !== observedButton) {
        if (buttonObserver) {
          buttonObserver.disconnect();
        }

        observedButton = sourceButton;

        if (sourceButton && 'MutationObserver' in window) {
          buttonObserver = new MutationObserver(sync);
          buttonObserver.observe(sourceButton, {
            attributes: true,
            attributeFilter: ['aria-disabled', 'class', 'disabled'],
            childList: true,
            subtree: true,
            characterData: true,
          });
        }
      }

      if (!sourceButton) {
        setDisabled(true);
        button.classList.remove('loading');
        if (spinner) spinner.classList.add('hidden');
        if (buttonText) buttonText.textContent = unavailableLabel;
        return;
      }

      const isLoading = sourceButton.classList.contains('loading');
      const isDisabled = sourceButton.disabled || sourceButton.getAttribute('aria-disabled') === 'true';

      setDisabled(isDisabled);
      button.classList.toggle('loading', isLoading);
      if (spinner) spinner.classList.toggle('hidden', !isLoading);
      if (buttonText) buttonText.textContent = getButtonLabel(sourceButton);
    };

    button.addEventListener('click', (event) => {
      event.preventDefault();

      const sourceButton = getMainButton();
      if (!sourceButton || sourceButton.disabled || sourceButton.getAttribute('aria-disabled') === 'true') {
        sync();
        return;
      }

      sourceButton.click();
      window.setTimeout(sync, 0);
    });

    const subscribeTo = (eventName) => {
      if (typeof subscribe !== 'function') return;
      if (typeof PUB_SUB_EVENTS === 'undefined') return;
      if (!PUB_SUB_EVENTS[eventName]) return;

      subscribe(PUB_SUB_EVENTS[eventName], () => {
        window.setTimeout(sync, 0);
      });
    };

    subscribeTo('optionValueSelectionChange');
    subscribeTo('variantChange');
    document.addEventListener('product-info:loaded', () => window.setTimeout(sync, 0));

    window.setTimeout(sync, 0);
    window.setTimeout(sync, 300);
  };

  const initAll = () => {
    document.querySelectorAll('.sa-before-after').forEach((section) => {
      initLazyMedia(section);
      initSlider(section);
      initProductAtc(section);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event && event.target) {
      const slider = event.target.querySelector('.sa-before-after');
      if (slider) {
        initLazyMedia(slider);
        initSlider(slider);
        initProductAtc(slider);
      }
    }
  });
})();